import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Checkbox from "@mui/material/Checkbox";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import ReplyIcon from "@mui/icons-material/Reply";
import { toast } from "react-toastify";
import JSZip from "jszip";
import {
  getAttachmentKey,
  sanitizeDownloadFileName,
  forceSaveBlob,
  getAttachmentFileBlob,
} from "../../../utils/attachmentDownload";
import "./CenterCard.scss";

// Replace <img src="cid:..."> with data URLs from attachments, or remove the tag if not found
function replaceCidImages(html: string, attachments: any[] = []) {
  if (!html) return html;
  const cidMap: Record<string, string> = {};
  if (attachments && attachments.length) {
    attachments.forEach((att) => {
      const cid = att.contentId || att.content_id || att.cid;
      if (cid && att.file && att.file_type) {
        cidMap[
          cid.replace(/[<>]/g, "")
        ] = `data:${att.file_type};base64,${att.file}`;
      }
    });
  }
  return html.replace(
    /<img([^>]*?)src=["']cid:([^"']+)["']([^>]*)>/gi,
    (match, before, cid, after) => {
      const url = cidMap[cid] || cidMap[cid.replace(/[<>]/g, "")];
      if (url) {
        return `<img${before}src=\"${url}\"${after}>`;
      } else {
        return "";
      }
    }
  );
}

// Decode MIME-encoded subject lines
const decodeMimeWords = (input?: string): string => {
  if (!input) return "";

  const normalized = input.replace(/\r?\n/g, " ").replace(/\s+/g, " ").trim();

  const decoded = normalized.replace(
    /=\?([^?]+)\?([bBqQ])\?([^?]+)\?=/g,
    (_, charset, encoding, text) => {
      try {
        const enc = encoding.toUpperCase();
        const decoder = new TextDecoder(charset.toLowerCase());

        if (enc === "B") {
          const binary = atob(text.replace(/\s/g, ""));
          const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
          return decoder.decode(bytes);
        } else {
          let cleaned = text.replace(/_/g, " ");
          const bytes: number[] = [];

          for (let i = 0; i < cleaned.length; i++) {
            const ch = cleaned[i];
            if (
              ch === "=" &&
              i + 2 < cleaned.length &&
              /[0-9A-Fa-f]{2}/.test(cleaned.slice(i + 1, i + 3))
            ) {
              bytes.push(parseInt(cleaned.slice(i + 1, i + 3), 16));
              i += 2;
            } else {
              bytes.push(ch.charCodeAt(0));
            }
          }

          return decoder.decode(new Uint8Array(bytes));
        }
      } catch {
        return text;
      }
    }
  );

  return decoded;
};

interface IProps {
  otherMailDetails: any;
  mailDetails: any[];
}

const CenterCard: React.FC<IProps> = ({ otherMailDetails, mailDetails }) => {
  const [openMails, setOpenMails] = useState<number[]>([]);
  const [attachmentSelection, setAttachmentSelection] = useState<Record<number, string[]>>({});

  useEffect(() => {
    setOpenMails(
      mailDetails.length > 0 ? [mailDetails[mailDetails.length - 1]?.id] : []
    );
  }, [mailDetails]);

  const toggleMailOpen = (id: number) => {
    setOpenMails((prev) =>
      prev.includes(id) ? prev.filter((mailId) => mailId !== id) : [...prev, id]
    );
  };

  const getInitials = (name: any) => {
    if (!name) return "?";
    const nameParts = name.split(" ");
    return nameParts.map((part) => part[0].toUpperCase()).join("");
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension || '')) {
      return <ImageIcon />;
    } else if (extension === 'pdf') {
      return <PictureAsPdfIcon />;
    } else {
      return <DescriptionIcon />;
    }
  };

  const formatFileSize = (base64String: string) => {
    if (!base64String) return "";
    const sizeInBytes = (base64String.length * 3) / 4;
    if (sizeInBytes < 1024) return `${sizeInBytes.toFixed(0)} B`;
    if (sizeInBytes < 1024 * 1024) return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getSelectedKeys = (mailId: number) =>
    new Set(attachmentSelection[mailId] ?? []);

  const setSelectedKeys = (mailId: number, keys: string[]) => {
    setAttachmentSelection((prev) => {
      const next = { ...prev };
      if (keys.length === 0) delete next[mailId];
      else next[mailId] = keys;
      return next;
    });
  };

  const toggleAttachmentKey = (mailId: number, key: string) => {
    const cur = getSelectedKeys(mailId);
    if (cur.has(key)) cur.delete(key);
    else cur.add(key);
    setSelectedKeys(mailId, Array.from(cur));
  };

  const toggleSelectAllAttachments = (mailId: number, attachments: any[]) => {
    const keys = attachments.map((a, i) => getAttachmentKey(a, i));
    const cur = getSelectedKeys(mailId);
    const allOn = keys.length > 0 && keys.every((k) => cur.has(k));
    setSelectedKeys(mailId, allOn ? [] : [...keys]);
  };

  const downloadSelectedAttachments = async (mail: any) => {
    const selected = getSelectedKeys(mail.id);
    const pairs = (mail.attachments as any[])
      .map((att, idx) => ({ att, idx }))
      .filter(
        ({ att, idx }) =>
          selected.has(getAttachmentKey(att, idx)) && att?.file
      );

    if (pairs.length === 0) {
      toast.error("No downloadable data for the selected attachments.");
      return;
    }

    try {
      const zip = new JSZip();
      const usedNames = new Set<string>();

      for (let i = 0; i < pairs.length; i++) {
        const { att } = pairs[i];
        const baseName = sanitizeDownloadFileName(
          att.file_name || `attachment-${i + 1}`
        );
        let entryName = baseName;
        let dup = 1;
        while (usedNames.has(entryName)) {
          dup += 1;
          const lastDot = baseName.lastIndexOf(".");
          entryName =
            lastDot > 0
              ? `${baseName.slice(0, lastDot)} (${dup})${baseName.slice(lastDot)}`
              : `${baseName} (${dup})`;
        }
        usedNames.add(entryName);

        const blob = await getAttachmentFileBlob(att.file);
        zip.file(entryName, blob);
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const zipBase = sanitizeDownloadFileName(
        decodeMimeWords(mail.subject || otherMailDetails?.subject) ||
          `email-${mail.id}`
      );
      forceSaveBlob(zipBlob, `${zipBase}-attachments.zip`);
      setSelectedKeys(mail.id, []);
      toast.success("Attachments saved as a ZIP file.");
    } catch {
      toast.error("Unable to download the attachments.");
    }
  };

  const getEmailDirection = (mail: any) => {
    return mail.is_received_email ? 'received' : 'sent';
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString || isNaN(new Date(dateString).getTime())) return "NA";
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    // If within last 24 hours, show relative time
    if (diffDays === 0) {
      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      return `${diffHours}h ago`;
    }
    
    // If within last week, show day name
    if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    }

    // Otherwise show full date
    return date.toLocaleDateString('en-US', { 
      day: '2-digit', 
      month: 'short',
      year: diffDays > 365 ? 'numeric' : undefined,
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  return (
    <div className="center-card-container">
      {/* Email Subject Header */}
      <div className="email-subject-section">
        <h1 className="email-subject">
          {decodeMimeWords(otherMailDetails?.subject) || "No Subject"}
        </h1>
        {/* <div className="email-metadata">
          <span className="ref-id">REF-ID: {otherMailDetails?.ref_id || "N/A"}</span>
        </div> */}
      </div>

      {/* Email Thread */}
      <div className="email-thread">
        {[...mailDetails].reverse().map((mail, index) => {
          const isOpen = openMails?.includes(mail.id);
          const direction = getEmailDirection(mail);
          const isLastEmail = index === mailDetails.length - 1;
          const attachmentsList = mail.attachments;
          const attKeys =
            attachmentsList?.length > 0
              ? attachmentsList.map((a: any, i: number) =>
                  getAttachmentKey(a, i)
                )
              : [];
          const attachmentSelectedSet = getSelectedKeys(mail.id);
          const selectedAttachmentCount = attKeys.filter((k) =>
            attachmentSelectedSet.has(k)
          ).length;
          const allAttachmentsSelected =
            attKeys.length > 0 &&
            selectedAttachmentCount === attKeys.length;
          const someAttachmentsSelected =
            selectedAttachmentCount > 0 &&
            selectedAttachmentCount < attKeys.length;

          return (
            <div
              key={mail.id}
              className={`email-message ${direction} ${isOpen ? 'expanded' : 'collapsed'} ${isLastEmail ? 'latest' : ''}`}
            >
              {/* Email Header */}
              <div className="email-header" onClick={() => toggleMailOpen(mail.id)}>
                <div className="header-left">
                  <div className={`sender-avatar ${direction}`}>
                    {getInitials(mail.receive_from)}
                  </div>
                  
                  <div className="sender-details">
                    <div className="sender-row">
                      <span className="sender-name">{mail.receive_from || "Unknown"}</span>
                      <span className={`email-badge ${direction}`}>
                        {direction === 'received' ? 'RECEIVED' : 'SENT'}
                      </span>
                    </div>
                    
                    {!isOpen && (
                      <div className="email-preview">
                        <span className="to-label">To:</span>
                        <span className="to-value">
                          {direction === 'received' 
                            ? mail.to?.slice(0, 2).join(", ") 
                            : mail.receive_from}
                          {mail.to?.length > 2 && ` +${mail.to.length - 2} more`}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="header-right">
                  <span className="email-time">{formatDateTime(mail.mail_sent_or_received_on)}</span>
                  {mail.attachments && mail.attachments.length > 0 && (
                    <div className="attachment-count">
                      <AttachFileIcon fontSize="small" />
                      <span>{mail.attachments.length}</span>
                    </div>
                  )}
                  <button className="expand-toggle">
                    {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </button>
                </div>
              </div>

              {/* Expanded Email Content */}
              {isOpen && (
                <div className="email-content-wrapper">
                  {/* Recipient Details */}
                  <div className="recipient-section">
                    <div className="recipient-row">
                      <span className="label">From:</span>
                      <span className="value">{mail.receive_from}</span>
                    </div>
                    <div className="recipient-row">
                      <span className="label">To:</span>
                      <span className="value">{mail.to?.join(", ") || "N/A"}</span>
                    </div>
                    {mail.cc && mail.cc.length > 0 && (
                      <div className="recipient-row">
                        <span className="label">CC:</span>
                        <span className="value">{mail.cc.join(", ")}</span>
                      </div>
                    )}
                    {mail.bcc && mail.bcc.length > 0 && (
                      <div className="recipient-row">
                        <span className="label">BCC:</span>
                        <span className="value">{mail.bcc.join(", ")}</span>
                      </div>
                    )}
                    <div className="recipient-row">
                      <span className="label">Sent:</span>
                      <span className="value">
                        {mail.mail_sent_or_received_on &&
                        !isNaN(new Date(mail.mail_sent_or_received_on).getTime())
                          ? new Date(mail.mail_sent_or_received_on).toLocaleString('en-US', { 
                              weekday: 'long',
                              day: 'numeric', 
                              month: 'long', 
                              year: 'numeric', 
                              hour: '2-digit', 
                              minute: '2-digit'
                            })
                          : "NA"}
                      </span>
                    </div>
                  </div>

                  {/* Email Body */}
                  <div className="email-body">
                    <div
                      className="email-html-content"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          replaceCidImages(mail.html_content, mail.attachments),
                          {
                            ALLOWED_ATTR: ["style", "href", "src", "alt", "class", "id"],
                          }
                        ),
                      }}
                    />
                  </div>

                  {/* Attachments */}
                  {mail.attachments && mail.attachments.length > 0 && (
                    <div className="attachments-container">
                      <div className="attachments-header">
                        <div className="attachments-header-left">
                          <Checkbox
                            size="small"
                            className="attachment-checkbox"
                            checked={allAttachmentsSelected}
                            indeterminate={someAttachmentsSelected}
                            onChange={() =>
                              toggleSelectAllAttachments(
                                mail.id,
                                mail.attachments
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                            inputProps={{
                              "aria-label": "Select all attachments",
                            }}
                          />
                          <AttachFileIcon fontSize="small" />
                          <span>{mail.attachments.length} Attachment{mail.attachments.length > 1 ? 's' : ''}</span>
                        </div>
                        {selectedAttachmentCount > 0 && (
                          <i className="bi bi-download cursor-pointer text-dark fs-3 me-8" onClick={(e) => {
                            e.stopPropagation();
                            downloadSelectedAttachments(mail);
                          }}></i>
                        )}
                      </div>
                      <div className="attachments-list">
                        {mail.attachments.map((attachment, idx) => {
                          const isImage = attachment.file_name.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                          const attKey = getAttachmentKey(attachment, idx);
                          const isChecked = attachmentSelectedSet.has(attKey);
                          
                          return (
                            <div key={attachment.id || idx} className="attachment-item">
                              <Checkbox
                                size="small"
                                className="attachment-checkbox attachment-row-checkbox"
                                checked={isChecked}
                                onChange={() =>
                                  toggleAttachmentKey(mail.id, attKey)
                                }
                                onClick={(e) => e.stopPropagation()}
                                inputProps={{
                                  "aria-label": `Select ${attachment.file_name}`,
                                }}
                              />
                              <div className="attachment-icon-wrapper">
                                {isImage ? (
                                  <div className="image-preview">
                                    <img
                                      src={attachment.file}
                                      alt={attachment.file_name}
                                    />
                                  </div>
                                ) : (
                                  <div className="file-icon">
                                    {getFileIcon(attachment.file_name)}
                                  </div>
                                )}
                              </div>
                              
                              <div className="attachment-info">
                                <div className="file-name" title={attachment.file_name}>
                                  {attachment.file_name}
                                </div>
                                <div className="file-size">
                                  {formatFileSize(attachment.file)}
                                </div>
                              </div>

                              <a
                                href={attachment.file}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="preview-btn"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <VisibilityIcon fontSize="small" />
                              </a>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CenterCard;